import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { RefreshCw, ChevronRight, MapPin, Package, Clock, Truck, Loader2, AlertCircle, Search } from 'lucide-react';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { bidService } from '../services/bidService';
import { documentService } from '../services/documentService';
import { shipmentServiceReal } from '../services/shipmentServiceReal';
import { authService } from '../services/authService';
import { driverService } from '../services/driverService';
import { clearStoredDriverMobileUser, getStoredDriverMobileUser } from '../utils/driverMobileSession';
import { isInternalDriverUser } from '../utils/userRoles';

interface OfferRequest {
  id: string;
  shipmentRecordId: string;
  shipmentId: string;
  date: string;
  weight: string;
  origin: string;
  originCity: string;
  destination: string;
  destinationCity: string;
  currentBestOffer?: number;
  myOffer?: number;
  assignedPrice?: number;
  shipmentStageLabel?: string;
  status: 'pending' | 'evaluated' | 'approved' | 'rejected';
  offerCount?: number;
}

interface ShipmentCardItem {
  id: string;
  shipmentId: string;
  date: string;
  deliveryDate?: string;
  origin: string;
  originCity: string;
  destination: string;
  destinationCity: string;
  weight: string;
  assignedPrice?: number;
  shipmentStageLabel?: string;
  documentCount?: number;
  accountingStatus?: string;
}

const MOBILE_STAGE_LABELS = [
  'Yük Almaya Gidiyor',
  'Yükleme Bekliyor',
  'Yükleme Başladı',
  'Yükleme Tamamlandı',
  'Teslimat Noktasına Gidiyor',
  'Teslimat Noktasına Ulaşıldı',
  'Boşaltma Bekliyor',
  'Boşaltma Başladı',
  'Boşaltma Tamamlandı',
];

const getShipmentStageLabel = (shipment: any) => {
  const stage = Number(shipment?.current_stage ?? 0);
  if (shipment?.status === 'delivered') return 'Teslimat Tamamlandı';
  if (stage === 3) return 'Yükleme Tamamlandı (Evrak Bekleniyor)';
  if (stage === 8) return 'Boşaltma Tamamlandı (Evrak Bekleniyor)';
  return MOBILE_STAGE_LABELS[stage] || 'Atandı';
};

const getAccountingStatusLabel = (value?: string) => {
  switch (String(value || '').trim()) {
    case 'delivery-approval-pending': return 'Teslim Onayı Bekliyor';
    case 'invoice-waiting': return 'Fatura Bekliyor';
    case 'invoice-approval-pending': return 'Faturalandırıldı';
    case 'payment-pre-approval': return 'Fatura Kontrol';
    case 'payment-pending': return 'Ödeme Bekliyor';
    case 'payment-completed': return 'Ödeme Tamamlandı';
    default: return 'İşlem Bekliyor';
  }
};

const getAccountingStatusBadge = (value?: string) => {
  switch (String(value || '').trim()) {
    case 'delivery-approval-pending': return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'invoice-waiting': return 'bg-sky-50 text-sky-800 border-sky-200';
    case 'invoice-approval-pending': return 'bg-purple-50 text-purple-800 border-purple-200';
    case 'payment-pre-approval': return 'bg-indigo-50 text-indigo-800 border-indigo-200';
    case 'payment-pending': return 'bg-orange-50 text-orange-800 border-orange-200';
    case 'payment-completed': return 'bg-green-50 text-green-800 border-green-200';
    default: return 'bg-slate-50 text-slate-700 border-slate-200';
  }
};

type TabType = 'offers' | 'active' | 'completed';

export function DriverMobileDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<TabType>('offers');
  const [offers, setOffers] = useState<OfferRequest[]>([]);
  const [activeShipments, setActiveShipments] = useState<ShipmentCardItem[]>([]);
  const [completedShipments, setCompletedShipments] = useState<ShipmentCardItem[]>([]);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const storedUser = getStoredDriverMobileUser<any>();
    if (!storedUser) {
      navigate('/driver-mobile-auth', { replace: true });
      return;
    }
    try {
      if (isInternalDriverUser(storedUser)) {
        navigate('/driver-mobile-home', { replace: true });
        return;
      }
      setUser(storedUser);
      loadOffers(storedUser);
    } catch (error) {
      console.error('Error loading user data:', error);
      navigate('/driver-mobile-auth', { replace: true });
    }
  }, [navigate]);

  const mapShipmentCard = (shipment: any): ShipmentCardItem => ({
    id: String(shipment.id),
    shipmentId: String(shipment.shipment_no || shipment.id),
    date: new Date(shipment.pickup_date || shipment.delivery_date || shipment.updated_at || shipment.created_at || Date.now()).toLocaleDateString('tr-TR'),
    deliveryDate: shipment.delivery_date ? new Date(shipment.delivery_date).toLocaleDateString('tr-TR') : '',
    weight: String(shipment.weight_kg || 0),
    origin: shipment.pickup_name || 'Yukleme Noktasi',
    originCity: shipment.pickup_city || '',
    destination: shipment.delivery_name || 'Teslimat Noktasi',
    destinationCity: shipment.delivery_city || '',
    assignedPrice: Number(shipment.final_price || shipment.fixed_price || 0),
    shipmentStageLabel: getShipmentStageLabel(shipment),
    accountingStatus: shipment.accounting_status || 'none',
  });

  const loadOffers = async (currentUser?: any) => {
    try {
      setLoading(true);
      setError(null);
      const response: any = await bidService.getBidRequests();
      const data = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
      const convertedOffers: OfferRequest[] = (Array.isArray(data) ? data : [])
        .map((bid: any) => {
          return {
            id: String(bid.id || bid.bid_request_id),
            shipmentRecordId: String(bid.shipment_id || bid.id),
            shipmentId: String(bid.shipment_no || bid.sevk_kodu || bid.shipment_id || bid.id),
            date: new Date(bid.deadline || bid.valid_until || bid.created_at || Date.now()).toLocaleDateString('tr-TR'),
            weight: String(bid.weight || bid.cargo_weight || bid.loadDetails?.weight || '0'),
            origin: bid.origin?.name || bid.loading_point || 'Yukleme Noktasi',
            originCity: bid.origin?.city || bid.loading_city || '',
            destination: bid.destination?.name || bid.delivery_point || 'Teslimat Noktasi',
            destinationCity: bid.destination?.city || bid.delivery_city || '',
            currentBestOffer: bid.lowestBid?.amount || bid.current_lowest_bid || bid.lowest_bid,
            offerCount: bid.totalBids || bid.offer_count || bid.total_bids || 0,
            myOffer: bid.my_bid_amount != null ? Number(bid.my_bid_amount) : undefined,
            status: 'pending',
          };
        });

      const effectiveUser = currentUser || user;
      if (effectiveUser?.id) {
        try {
          const driverResponse: any = await driverService.getDriverByUserId(String(effectiveUser.id));
          const driver = driverResponse?.data || driverResponse || {};

          if (driver?.id) {
            const shipmentsResponse: any = await shipmentServiceReal.getShipments({ driverId: String(driver.id), assignmentResponseStatus: 'accepted' });
            const shipmentRows = Array.isArray(shipmentsResponse?.data) ? shipmentsResponse.data : [];
            const activeRows = shipmentRows.filter((shipment: any) => {
              const processStatus = String(shipment.process_status || '').trim();
              const shipmentStatus = String(shipment.status || '').trim().toLowerCase();
              return processStatus !== 'bosaltma-tamamlandi' && shipmentStatus !== 'delivered' && shipmentStatus !== 'cancelled';
            });
            const completedRows = shipmentRows.filter((shipment: any) => {
              const processStatus = String(shipment.process_status || '').trim();
              const shipmentStatus = String(shipment.status || '').trim().toLowerCase();
              return processStatus === 'bosaltma-tamamlandi' || shipmentStatus === 'delivered';
            });

            setActiveShipments(activeRows.map(mapShipmentCard));

            const completedCards = completedRows.map(mapShipmentCard);
            const documentCounts = await Promise.all(completedCards.map(async (shipment) => {
              try {
                const response: any = await documentService.getDocuments({ entityType: 'shipment', entityId: shipment.id });
                const items = Array.isArray(response?.data) ? response.data : [];
                return [shipment.id, items.length] as const;
              } catch {
                return [shipment.id, 0] as const;
              }
            }));
            const countMap = Object.fromEntries(documentCounts);
            setCompletedShipments(completedCards.map((shipment) => ({ ...shipment, documentCount: Number(countMap[shipment.id] || 0) })));

          }
        } catch (shipmentError) {
          console.error('Failed to load direct assigned shipments:', shipmentError);
          setActiveShipments([]);
          setCompletedShipments([]);
        }
      }

      setOffers(convertedOffers);
    } catch (err) {
      console.error('Failed to load offers:', err);
      setError('Sevkiyatlar yuklenemedi');
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadOffers(user);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const handleLogout = () => {
    authService.logout();
    clearStoredDriverMobileUser();
    navigate('/driver-mobile-auth');
  };

  const getFilteredOffers = () => {
    const query = search.trim().toLocaleLowerCase('tr-TR');
    if (activeTab === 'offers') {
      if (!query) return offers;
      return offers.filter((offer) => [offer.shipmentId, offer.origin, offer.originCity, offer.destination, offer.destinationCity, offer.weight].join(' ').toLocaleLowerCase('tr-TR').includes(query));
    }

    const scoped = activeTab === 'active' ? activeShipments : completedShipments;
    if (!query) return scoped;
    return scoped.filter((shipment) => [shipment.shipmentId, shipment.origin, shipment.originCity, shipment.destination, shipment.destinationCity, shipment.weight].join(' ').toLocaleLowerCase('tr-TR').includes(query));
  };

  const getTabCount = (tab: TabType) => {
    if (tab === 'offers') return offers.length;
    if (tab === 'active') return activeShipments.length;
    return completedShipments.length;
  };

  if (!user) return null;

  const filteredOffers = getFilteredOffers();

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-blue-900 dark:to-blue-800 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Sevkiyatlar</h1>
            <p className="text-sm text-slate-500 dark:text-blue-200">Teklifleri, devam eden ve tamamlanan sevkiyatları takip edin</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-lg p-2 text-slate-700 transition-colors hover:bg-slate-100 dark:text-white dark:hover:bg-blue-800"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-[88px] z-10">
          <div className="grid grid-cols-3">
            <button
            onClick={() => setActiveTab('offers')}
            className={`py-3 text-center transition-colors ${
              activeTab === 'offers'
                ? 'border-b-2 border-blue-600 text-blue-600 font-bold'
                : 'text-gray-600'
            }`}
          >
            <div className="text-xs">Teklifler</div>
            <div className="text-lg font-bold">{getTabCount('offers')}</div>
          </button>

          <button
            onClick={() => setActiveTab('active')}
            className={`py-3 text-center transition-colors ${
              activeTab === 'active'
                ? 'border-b-2 border-green-600 text-green-600 font-bold'
                : 'text-gray-600'
            }`}
          >
            <div className="text-xs">Devam Eden</div>
            <div className="text-lg font-bold">{getTabCount('active')}</div>
          </button>

          <button
            onClick={() => setActiveTab('completed')}
            className={`py-3 text-center transition-colors ${
              activeTab === 'completed'
                ? 'border-b-2 border-slate-700 text-slate-700 font-bold'
                : 'text-gray-600'
            }`}
          >
             <div className="text-xs">Tamamlanan</div>
            <div className="text-lg font-bold">{getTabCount('completed')}</div>
          </button>
        </div>
        <div className="px-4 pb-4">
          <label className="relative block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sevkiyat no, yükleme veya teslimat ara"
              className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm text-gray-700 outline-none focus:border-blue-400"
            />
          </label>
        </div>
      </div>

      {/* Offer Cards */}
      <div className="px-4 py-4 space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
            <p className="text-gray-500">Sevkiyatlar yukleniyor</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-300 mx-auto mb-3" />
            <p className="text-gray-700 mb-3">{error}</p>
            <button onClick={() => { loadOffers(user); loadMyBids(user); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Tekrar dene</button>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Bu kategoride sevkiyat bulunmuyor</p>
          </div>
        ) : activeTab === 'offers' ? (
          filteredOffers.map((offer) => (
            <div
              key={offer.id}
              className="w-full bg-white rounded-xl shadow-sm overflow-hidden"
            >
              {(() => {
                const detailTarget = `/driver-mobile-offer/${offer.id}`;

                return (
              <button
                onClick={() => navigate(detailTarget)}
                className="w-full hover:bg-gray-50 transition-colors text-left"
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 text-blue-900 font-bold px-3 py-1 rounded-lg text-sm">
                      {offer.shipmentId}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      {offer.date}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{offer.weight} ton</span>
                    {offer.offerCount !== undefined && offer.offerCount > 0 && (
                      <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                        {offer.offerCount} teklif
                      </span>
                    )}
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>

                {/* Current Best Offer */}
                {offer.currentBestOffer && (
                  <div className="px-4 py-2 bg-green-50 border-b border-green-100">
                    <p className="text-sm font-bold text-green-800">
                      ● En düşük teklif: {offer.currentBestOffer.toLocaleString('tr-TR')} TL
                    </p>
                  </div>
                )}

                {/* My Offer (if exists) */}
                {offer.myOffer && (
                  <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                    <p className="text-sm font-bold text-blue-800">
                      Teklifiniz: {offer.myOffer.toLocaleString('tr-TR')} TL
                    </p>
                  </div>
                )}

                {!offer.myOffer && offer.assignedPrice && (
                  <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                    <p className="text-sm font-bold text-blue-800">
                      Anlasilan Ucret: {offer.assignedPrice.toLocaleString('tr-TR')} TL
                    </p>
                  </div>
                )}

                {/* Route */}
                <div className="px-4 py-4 flex items-center gap-4">
                  {/* Origin */}
                  <div className="flex-1">
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5" />
                      <div className="flex-1">
                        <p className="text-sm font-bold text-gray-900">{offer.originCity}</p>
                        <p className="text-xs text-gray-600">{offer.origin}</p>
                      </div>
                    </div>
                  </div>

                  <div className="text-gray-400">→</div>

                  {/* Destination */}
                  <div className="flex-1">
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5" />
                      <div className="flex-1">
                        <p className="text-sm font-bold text-gray-900">{offer.destinationCity}</p>
                        <p className="text-xs text-gray-600">{offer.destination}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
                );
              })()}

            </div>
          ))
        ) : (
          filteredOffers.map((shipment: any) => (
            <button
              key={shipment.id}
              onClick={() => navigate(`/driver-mobile-shipment-tracking/${shipment.id}`)}
              className="w-full bg-white rounded-xl shadow-sm overflow-hidden text-left"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="flex items-center gap-3">
                  <div className={`font-bold px-3 py-1 rounded-lg text-sm ${activeTab === 'active' ? 'bg-green-100 text-green-900' : 'bg-slate-100 text-slate-900'}`}>
                    {shipment.shipmentId}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    {shipment.date}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>

              {shipment.assignedPrice ? (
                <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                  <p className="text-sm font-bold text-blue-800">Anlaşılan Ücret: {shipment.assignedPrice.toLocaleString('tr-TR')} TL</p>
                </div>
              ) : null}

              <div className={`px-4 py-2 border-b ${activeTab === 'active' ? 'bg-emerald-50 border-emerald-100' : 'bg-slate-50 border-slate-100'}`}>
                <p className={`text-sm font-bold ${activeTab === 'active' ? 'text-emerald-800' : 'text-slate-700'}`}>{shipment.shipmentStageLabel || (activeTab === 'completed' ? 'Tamamlandı' : 'Devam Ediyor')}</p>
              </div>

              {activeTab === 'completed' ? (
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/70">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500">Teslim Tarihi</p>
                      <p className="mt-1 font-semibold text-gray-900">{shipment.deliveryDate || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Yüklenen Evrak</p>
                      <p className="mt-1 font-semibold text-gray-900">{shipment.documentCount || 0} dosya</p>
                    </div>
                  </div>
                  <span className={`mt-3 inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getAccountingStatusBadge(shipment.accountingStatus)}`}>
                    {getAccountingStatusLabel(shipment.accountingStatus)}
                  </span>
                </div>
              ) : null}

              <div className="px-4 py-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-gray-900">{shipment.originCity}</p>
                      <p className="text-xs text-gray-600">{shipment.origin}</p>
                    </div>
                  </div>
                </div>
                <div className="text-gray-400">→</div>
                <div className="flex-1">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-gray-900">{shipment.destinationCity}</p>
                      <p className="text-xs text-gray-600">{shipment.destination}</p>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Bottom Navigation */}
      <DriverBottomNav />
    </div>
  );
}
